<template>
  <div class="cart-sidebar relative z-[100]">
    <Overlay :visible="isOpen" @click="closeSidebar" />
    <Drawer
      ref="eventsSidebarRef"
      class="events-sidebar"
      :is-open="isOpen"
      data-test-id="reviews-sidebar"
      @click:close="closeSidebar"
    >
      <template #header>
        <div class="reviews-sidebar__top">
          <div class="reviews-sidebar__header">
            <div v-if="currentStep === 'detail'" class="back" @click="onBack">
              <IconBack />
            </div>
            <h4 class="p-4">{{ t('review.form.selectAnotherEvent') }}</h4>
          </div>
        </div>
      </template>

      <div v-for="event in events" :key="event?.id" class="space-y-4">
        <div class="event-card" @click="emit('click:event', event)">
          <div class="card-image-wrapper">
            <NuxtImg
              class="card-image"
              :src="eventGetters.getFirstThumb(event)"
            />
          </div>
          <div class="shrink">
            {{ event?.title }}
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>

<script lang="ts" setup>
import type { EventDescription, Review } from '~/composables/types/event'
import IconBack from '~/assets/icons/back.svg'

interface Props {
  events: EventDescription[]
  isOpen: boolean
  selectedEventId?: string
}

const { t } = useI18n()

const props = defineProps<Props>()

const currentStep = ref<'list' | 'detail'>('list')
const detailReview = ref<Review>({})

const onBack = () => {
  currentStep.value = 'list'
  detailReview.value = {} as Review
}

const emit = defineEmits<{
  (e: 'click:event', value: EventDescription): void
  (e: 'close'): void
}>()

watch(
  () => props.isOpen,
  () => {
    if (props.isOpen) {
      document.body.classList.add('prevent-scroll')
    } else {
      document.body.classList.remove('prevent-scroll')
    }
  }
)

const closeSidebar = () => {
  setTimeout(() => {
    currentStep.value = 'list'
    detailReview.value = {} as Review
  }, 500)
  emit('close')
}
</script>

<style lang="postcss" scoped>
.events-sidebar {
  @apply w-full md:max-w-[552px] p-0 overflow-y-scroll;

  &__top {
    @apply relative flex gap-2 items-center px-6 py-4 w-full top-0 border-b border-gray-200;
  }

  &__header {
    @apply w-full flex justify-center;

    .label {
      @apply text-lg font-semibold text-center;
    }

    .back {
      @apply absolute left-2 top-0 bottom-0 my-auto cursor-pointer w-[22px] h-[22px];

      svg {
        @apply w-full h-full;
      }
    }
  }
}
.event-card {
  @apply hover:bg-gray-200 flex mx-4 p-4 rounded-lg cursor-pointer ease-linear duration-200;
}
.card-image-wrapper {
  @apply relative min-w-[172px] w-[172px] h-[136px] rounded-lg overflow-hidden bg-gray-200;
  @apply mr-4;
}
.card-image {
  @apply relative block w-full h-full object-cover duration-300 ease-in-out;
}
</style>

<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>
